import type {
  AddressAnalyzeRequest,
  GeocodeResponse,
  Point,
  ReinfoOptions,
  ReverseGeocodeResponse,
  SiteAnalysisResponse,
  SiteAnalyzeRequest,
  SiteCompareRequest,
  SiteCompareResponse,
} from './api.types';
import { readDevSettings } from './dev-settings';

export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string) {
    super(`API error ${status}: ${body.slice(0, 200)}`);
    this.status = status;
    this.body = body;
  }
}

function baseUrl(): string {
  const fromDev = readDevSettings().apiBase;
  if (fromDev) return fromDev.replace(/\/+$/, '');
  const fromEnv = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  // 既定は配信ベースパスの配下 (`/<base>/api/...`)。共有ドメインのサブディレクトリで配信するとき、
  // オリジン直下の /api は他サービスのものなので使わない。base が '/' なら従来どおり '/api/...'。
  return import.meta.env.BASE_URL.replace(/\/+$/, '');
}

function effectiveReinfo(opts?: ReinfoOptions): ReinfoOptions {
  const dev = readDevSettings();
  return {
    enabled: true,
    zoom: 14,
    layer_set: dev.fullLayer ? 'full' : 'essential',
    ...opts,
  };
}

function effectiveMock(explicit?: boolean): boolean {
  if (explicit !== undefined) return explicit;
  return readDevSettings().mock;
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const url = baseUrl() + path;
  const response = await fetch(url, {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : (rest.body ?? null),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, body);
  }
  return (await response.json()) as T;
}

export const api = {
  async geocode(q: string, limit = 1): Promise<GeocodeResponse> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return request<GeocodeResponse>(`/api/v1/geocode?${params.toString()}`);
  },

  async reverseGeocode(point: Point): Promise<ReverseGeocodeResponse> {
    const params = new URLSearchParams({
      lon: String(point.lon),
      lat: String(point.lat),
    });
    return request<ReverseGeocodeResponse>(
      `/api/v1/reverse-geocode?${params.toString()}`,
    );
  },

  async analyze(req: SiteAnalyzeRequest): Promise<SiteAnalysisResponse> {
    const body: SiteAnalyzeRequest = {
      ...req,
      mock: effectiveMock(req.mock),
      reinfo: effectiveReinfo(req.reinfo),
    };
    return request<SiteAnalysisResponse>('/api/v1/sites/analyze', {
      method: 'POST',
      json: body,
    });
  },

  async analyzeAddress(req: AddressAnalyzeRequest): Promise<SiteAnalysisResponse> {
    const body: AddressAnalyzeRequest = {
      ...req,
      mock: effectiveMock(req.mock),
      reinfo: effectiveReinfo(req.reinfo),
    };
    return request<SiteAnalysisResponse>('/api/v1/sites/analyze-address', {
      method: 'POST',
      json: body,
    });
  },

  async compare(req: SiteCompareRequest): Promise<SiteCompareResponse> {
    const body: SiteCompareRequest = {
      ...req,
      mock: effectiveMock(req.mock),
      reinfo: effectiveReinfo(req.reinfo),
    };
    return request<SiteCompareResponse>('/api/v1/sites/compare', {
      method: 'POST',
      json: body,
    });
  },

  async sampleReport(): Promise<SiteAnalysisResponse> {
    return request<SiteAnalysisResponse>('/api/v1/reports/sample');
  },
};
